import { AccentVariantEnum } from '@delmonicos/shared-design-system-react/src/components/accent-icon/accent-icon';
import { Card } from '@delmonicos/shared-design-system-react/src/components/card/card';
import { Icon } from '@delmonicos/shared-design-system-react/src/components/icon/icon';
import {
  GapEnum,
  Stack,
} from '@delmonicos/shared-design-system-react/src/components/stack/stack';
import {
  Text,
  TextColor,
} from '@delmonicos/shared-design-system-react/src/components/typography/text/text';
import { AccentTitle } from '@delmonicos/shared-design-system-react/src/components/typography/title/accent-title/accent-title';
import { HeadingEnum } from '@delmonicos/shared-design-system-react/src/models/enums/heading.enum';
import { TariffElementModel } from '@delmonicos/shared-sdk/libs/cpms/src/libs/charge-hub-service-lib/src/wrapper/outputs/tariff/tariff-element.model';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { DAYS } from '@app/feature/location/days';
import { PriceDetailsComponent } from '@app/feature/tariff/tariff-details/cards/price-details.component';
import { useTariffContext } from '@app/feature/tariff/tariff.context';

export const ComplexTariffComponent = (): React.JSX.Element => {
  const { t } = useTranslation();
  const tariff = useTariffContext();

  const elementsGroupedByDays = useMemo(() => {
    const groups = new Map<
      string,
      { keyDays: string[]; items: TariffElementModel[] }
    >();

    for (const el of tariff.elements) {
      const days = el.restrictions?.dayOfWeek ?? [];
      const sortedDays = [...days].sort(
        (a, b) => DAYS.indexOf(a) - DAYS.indexOf(b),
      );
      const key = JSON.stringify(sortedDays);
      if (!groups.has(key)) {
        groups.set(key, { keyDays: sortedDays, items: [] });
      }
      groups.get(key)!.items.push(el);
    }

    return Array.from(groups.values()).map((group) => group.items);
  }, [tariff]);

  const getDaysOf = (element: TariffElementModel) => {
    if (
      !element.restrictions?.dayOfWeek ||
      element.restrictions?.dayOfWeek.length === 0
    ) {
      return null;
    }

    return element.restrictions.dayOfWeek
      .map((day) =>
        t(`delmonicos-shared-sdk.ocpi-protocol-enums.day-of-week.${day}`),
      )
      .join(' - ');
  };

  const getDaysOfRange = (elements: TariffElementModel[]) => {
    const firstElement = elements[0];

    if (!firstElement) {
      return null;
    }

    return getDaysOf(firstElement);
  };

  return (
    <Stack gap={GapEnum.M} alignItems="start">
      {elementsGroupedByDays.map((elements, index) => (
        <Card flex={1} gap={GapEnum.M} key={`${elements.length}-${index}`}>
          <Stack
            gap={GapEnum.S}
            justifyContent="space-between"
            flex={1}
            alignItems="center">
            <AccentTitle
              icon="calendar-date"
              text={getDaysOfRange(elements) ?? '-'}
              variant={
                index === 0
                  ? AccentVariantEnum.Accent1
                  : AccentVariantEnum.Accent2
              }
            />
            {elementsGroupedByDays.length > 1 && (
              <Text color={TextColor.Secondary}>
                {t('$.tariff-details.price.range.number', {
                  number: index + 1,
                })}
              </Text>
            )}
          </Stack>

          {elements.map((element, elementIndex) => (
            <Card gap={GapEnum.M} key={`${elementIndex}-${index}`}>
              <Stack
                gap={GapEnum.S}
                justifyContent="space-between"
                alignItems="center">
                <Stack gap={GapEnum.S} alignItems="center">
                  <AccentTitle
                    icon="price"
                    as={HeadingEnum.H2}
                    text={t('$.tariff-details.price.range.time-slot.title')}
                  />
                  {element.restrictions?.startTime &&
                    element.restrictions?.endTime && (
                      <Stack gap={GapEnum.S} alignItems="center">
                        <Icon name="clock" />
                        <Text bold>
                          {element.restrictions.startTime} -{' '}
                          {element.restrictions.endTime}
                        </Text>
                      </Stack>
                    )}
                </Stack>

                {elements.length > 1 && (
                  <Text color={TextColor.Secondary}>
                    {t('$.tariff-details.price.range.time-slot.number', {
                      number: elementIndex + 1,
                    })}
                  </Text>
                )}
              </Stack>

              <PriceDetailsComponent
                priceComponents={element.priceComponents}
                currency={tariff.currency}
              />
            </Card>
          ))}
        </Card>
      ))}
    </Stack>
  );
};
